import { FilterTh } from '../common/column-filter-indicator'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  rosterPagination,
  useDebouncedRosterSearch,
} from '@speaking-coach/roster-list'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { queryKeys } from '../api/query-keys.js'
import {
  FilteredEmptyState,
  ListPage,
  ListPagination,
  SearchField,
} from '../common/list-page.js'
import {
  ROW_LINK,
  TABLE,
  TABLE_HEAD_ROW,
  TABLE_ROW,
  TABLE_WRAP,
} from '../common/table.js'
import { Badge, buttonVariants, Icon, Notice } from '@speaking-coach/ui'
import { adminUserAtom } from '../state/atoms.js'
import { AdminAccessState } from './admin-access-state.js'
import { coooriAdminsApi, messageForCoooriAdminError } from './api.js'
import { formatAdminDate } from './format.js'
import type { CoooriAdmin } from './types.js'

const PAGE_SIZE = 50

function lastInvitation(admin: CoooriAdmin): JSX.Element {
  const delivery = admin.lastDelivery
  if (delivery === null) return <span>—</span>
  const timestamp = delivery.sentAt ?? delivery.failedAt ?? delivery.scheduledFor
  return (
    <div>
      <p>{formatAdminDate(timestamp)}</p>
      <p className="mt-0.5 text-caption text-text-secondary">
        {delivery.sentAt !== null
          ? 'Sent'
          : delivery.failedAt !== null
            ? 'Failed'
            : 'Queued'}
      </p>
    </div>
  )
}

export function CoooriAdminsListPage(): JSX.Element {
  const currentAdmin = useAtomValue(adminUserAtom)
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  const search = useDebouncedRosterSearch(query)

  const params = {
    search: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }
  const adminsQuery = useQuery({
    queryKey: queryKeys.coooriAdminList(params),
    queryFn: () => coooriAdminsApi.list(params),
    placeholderData: keepPreviousData,
  })
  const admins = adminsQuery.data?.admins ?? []
  const total = adminsQuery.data?.total ?? 0
  const paginationState = rosterPagination({
    total,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  })

  return (
    <ListPage
      title="Cooori admins"
      count={adminsQuery.data === undefined ? undefined : total}
      action={
        <Link
          to="/admin/admins/new"
          className={buttonVariants({ variant: 'primary', size: 'default' })}
        >
          <Icon name="add" /> Invite admin
        </Link>
      }
      search={
        <SearchField
          id="cooori-admin-search"
          label="Search Cooori admins"
          placeholder="Search family name, given name or email…"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value)
            setPage(0)
          }}
          surface="raised"
        />
      }
      pagination={
        !adminsQuery.isPending && !adminsQuery.isError && admins.length > 0 ? (
          <ListPagination
            summary={
              <>
                Showing {paginationState.from}–{paginationState.to} of {total}
              </>
            }
            page={page}
            pageCount={paginationState.pageCount}
            previousDisabled={paginationState.previousDisabled}
            nextDisabled={paginationState.nextDisabled}
            onPrevious={() => setPage(paginationState.page - 1)}
            onNext={() => setPage(paginationState.page + 1)}
          />
        ) : undefined
      }
    >
      <>
        {adminsQuery.isPending && (
          <p className="text-text-secondary">Loading Cooori admins…</p>
        )}
        {adminsQuery.isError && (
          <Notice variant="danger">
            <strong>Could not load Cooori admins.</strong>{' '}
            {messageForCoooriAdminError(adminsQuery.error)}
          </Notice>
        )}
        {!adminsQuery.isPending && !adminsQuery.isError && admins.length === 0 && (
          <FilteredEmptyState
            hasFilters={search !== ''}
            noun="Cooori admins"
            onClear={() => {
              setQuery('')
              setPage(0)
            }}
            emptyHint="No Cooori admins yet. Use Invite admin to add one."
          />
        )}
        {admins.length > 0 && (
          <div
            className={
              adminsQuery.isPlaceholderData
                ? 'opacity-60 transition-opacity'
                : 'transition-opacity'
            }
          >
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
                <thead>
                  <tr className={TABLE_HEAD_ROW}>
                    <FilterTh className="p-3 font-medium" label="Email" filterDescription={search ? `Search: “${search}”` : undefined} />
                    <FilterTh className="p-3 font-medium" label="Family name" filterDescription={search ? `Search: “${search}”` : undefined} />
                    <FilterTh className="p-3 font-medium" label="Given name" filterDescription={search ? `Search: “${search}”` : undefined} />
                    <th className="p-3 font-medium">Access</th>
                    <th className="p-3 font-medium">Created</th>
                    <th className="p-3 font-medium">Last invitation</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr key={admin.userId} className={TABLE_ROW}>
                      <td className="p-3">
                        <Link
                          to={`/admin/admins/${admin.userId}`}
                          className={ROW_LINK}
                        >
                          {admin.email}
                        </Link>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {admin.lastName ?? '—'}
                          {admin.userId === currentAdmin?.id && (
                            <Badge variant="neutral">You</Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">{admin.firstName ?? '—'}</td>
                      <td className="p-3">
                        <AdminAccessState admin={admin} />
                      </td>
                      <td className="p-3 text-table text-text-secondary">
                        {formatAdminDate(admin.createdAt)}
                      </td>
                      <td className="p-3 text-table text-text-secondary">
                        {lastInvitation(admin)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    </ListPage>
  )
}
